import { DesignToCodeApi } from '../../../apis/designToCodeConfigApi';

type FigmaNode = any;
let imagesCache: string[] = [];

interface HtmlCssResult {
  html: string;
  css: string;
}

function colorToCss(color: any): string {
  if (!color) return '';
  const r = Math.round((color.r || 0) * 255);
  const g = Math.round((color.g || 0) * 255);
  const b = Math.round((color.b || 0) * 255);
  if (color.a !== undefined && color.a < 1) {
    return `rgba(${r}, ${g}, ${b}, ${color.a})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
}

function getColorFill(nodes: FigmaNode[], colorFill = null): any {
  for (const child of nodes) {
    if (
      (child.type === 'TEXT' || child.type === 'VECTOR') &&
      child.fills &&
      child.fills.length > 0
    ) {
      const fill = child.fills.find(
        (f: any) =>
          f.type === 'SOLID' &&
          (f.visible !== false || typeof f.visible === 'undefined'),
      );
      if (fill) {
        colorFill = fill;
      }
    } else if (child.type === 'FRAME') {
      colorFill = child.children?.length ? getColorFill(child.children) : null;
    }
  }
  return colorFill;
}

function getBorderRadius(nodes: FigmaNode[], style = ''): any {
  for (const child of nodes) {
    if (child.type === 'ELLIPSE') {
      return `border-radius: 9999px;`;
    }
    if (child.type === 'RECTANGLE' || child.type === 'FRAME') {
      if ('cornerRadius' in child)
        style = `border-radius: ${child.cornerRadius}px;`;
      else {
        if ('topLeftRadius' in child) {
          style += `border-top-left-radius: ${child.topLeftRadius}px;`;
        }
        if ('topRightRadius' in child) {
          style += `border-top-right-radius: ${child.topRightRadius}px;`;
        }
        if ('bottomLeftRadius' in child) {
          style += `border-bottom-left-radius: ${child.bottomLeftRadius}px;`;
        }
        if ('bottomRightRadius' in child) {
          style += `border-bottom-right-radius: ${child.bottomRightRadius}px;`;
        }
      }
    }
    if (!style && child.children && child.children.length > 0) {
      style = getBorderRadius(child.children, style);
    }
  }
  return style;
}

// Helper to recursively collect fills from children
function collectFillsFromChildren(nodes: FigmaNode[]): any {
  const colorBackgroundFills: any = {
    backgroundFills: [],
    colorFills: [],
  };
  for (const child of nodes) {
    // Only consider fills that are visible or have no visible property
    if (child.visible !== false && child.fills && child.fills.length > 0) {
      if (child.type === 'SECTION' || child.type === 'RECTANGLE') {
        colorBackgroundFills.backgroundFills.push(
          ...child.fills.filter(
            (f: any) => f.visible !== false || typeof f.visible === 'undefined',
          ),
        );
      }
      if (
        child.type === 'TEXT' ||
        child.type === 'VECTOR' ||
        child.type === 'FRAME'
      ) {
        colorBackgroundFills.colorFills.push(
          ...child.fills.filter(
            (f: any) => f.visible !== false || typeof f.visible === 'undefined',
          ),
        );
      }
    }
    // Recursively collect from inner children
    if (child.children?.length > 0) {
      const fills = collectFillsFromChildren(child.children);
      colorBackgroundFills.backgroundFills.push(...fills.backgroundFills);
      colorBackgroundFills.colorFills.push(...fills.colorFills);
    }
  }
  return colorBackgroundFills;
}
/**
 * Coding standards based on Figma plugin docs:
 * - Use semantic HTML tags for UI elements.
 * - Avoid unnecessary wrapper divs.
 * - Only apply styles that exist in the node.
 * - Use flexbox for layoutMode nodes.
 * - Avoid extra padding/margin unless specified.
 * - Use node.id for unique class names if available.
 * - Only render visible nodes.
 * - Prefer extracting text from TEXT nodes for labels/buttons.
 * - Avoid inline styles for accessibility, but use for codegen simplicity.
 */

function styleFromNode(
  node: FigmaNode,
  isImage = false,
  isButton = false,
): string {
  let style = '';
  if (node.layoutPositioning === 'ABSOLUTE' || node.isFixed) {
    style += node.isFixed ? 'position: fixed;' : 'position: absolute;';
    const box = node.absoluteBoundingBox || {};
    if (node.absoluteRenderBounds) {
      const x = Math.abs(box.x) - Math.abs(node.absoluteRenderBounds.x);
      const y = Math.abs(box.y) - Math.abs(node.absoluteRenderBounds.y);
      style += `left: ${x}px; top: ${y}px;`;
    }
  } else {
    if (
      node.layoutPositioning === 'AUTO' &&
      !node.absoluteBoundingBox?.length
    ) {
      style += 'position: static;';
    } else {
      style += 'position: relative;';
    }
  }
  const box =
    node.layoutPositioning === 'ABSOLUTE' || node.isFixed
      ? node.absoluteRenderBounds || node.absoluteBoundingBox || {}
      : node.absoluteBoundingBox || {};
  if (box.width) style += `width: ${box.width}px;`;
  if (box.height && node.type !== 'TEXT' && !isButton) {
    style += `height: ${box.height}px;`;
  }
  if (node.cornerRadius) style += `border-radius: ${node.cornerRadius}px;`;

  // --- Button color/background-color logic ---
  if (isButton) {
    // Get fills from node or recursively from children if not present
    let buttonFills: any[] = [];
    let backgroundFill = null;
    let colorFill = null;
    const colorBackgroundFills = collectFillsFromChildren(node.children || []);
    if (
      node.visible !== false &&
      node.type === 'RECTANGLE' &&
      node.fills &&
      node.fills.length > 0
    ) {
      buttonFills = node.fills.filter(
        (f: any) => f.visible !== false || typeof f.visible === 'undefined',
      );
      backgroundFill = buttonFills?.find((f: any) => f.type === 'SOLID');
    }
    if (!buttonFills?.length && node.children && node.children.length > 0) {
      buttonFills = colorBackgroundFills.backgroundFills;
      backgroundFill = buttonFills?.find((f: any) => f.type === 'SOLID');
    }

    // Apply background-color from fills (background for non-VECTOR, color for VECTOR)

    if (node.children && node.children.length > 0) {
      // Try to find a color fill from children
      colorFill = getColorFill(node.children);
      if (!colorFill) {
        colorFill = colorBackgroundFills.colorFills.find(
          (f: any) => f.type === 'SOLID',
        );
      }
      style += getBorderRadius(node.children) || '';
    }

    if (backgroundFill?.color) {
      style += `background-color: ${colorToCss(backgroundFill.color)};`;
    } else if (node.backgroundColor) {
      style += `background-color: ${colorToCss(node.backgroundColor)};`;
    }
    // For TEXT nodes, avoid setting color if same as background
    if (colorFill && colorFill.color) {
      if (
        node.type === 'TEXT' &&
        backgroundFill &&
        backgroundFill.color &&
        colorToCss(backgroundFill.color) === colorToCss(colorFill.color)
      ) {
        // Skip setting color if same as background
      } else {
        style += `color: ${colorToCss(colorFill.color)};`;
      }
    }

    // Border color from strokes
    if (node.strokes && node.strokes.length > 0) {
      const stroke = node.strokes.find(
        (s: any) =>
          s.type === 'SOLID' &&
          (s.visible !== false || typeof s.visible === 'undefined'),
      );
      if (stroke && stroke.color)
        style += `border: 1px solid ${colorToCss(stroke.color)};`;
    }
  }
  // --- End button logic ---

  // For TEXT nodes: use fills for color, background for background-color if present
  else if (node.type === 'TEXT') {
    if (node.fills && node.fills.length > 0) {
      const fill = node.fills.find(
        (f: any) =>
          f.type === 'SOLID' &&
          (f.visible !== false || typeof f.visible === 'undefined'),
      );
      if (fill && fill.color) style += `color: ${colorToCss(fill.color)};`;
    }
    if (node.background && node.background.length > 0) {
      const bgFill = node.background.find(
        (f: any) =>
          f.type === 'SOLID' &&
          (f.visible !== false || typeof f.visible === 'undefined'),
      );
      if (bgFill && bgFill.color)
        style += `background-color: ${colorToCss(bgFill.color)};`;
    }
    if (node.fills && node.fills.length > 0) {
      const gradientFill = node.fills.find(
        (f: any) => f.type && f.type.startsWith('GRADIENT'),
      );
      if (gradientFill && gradientFill.gradientStops) {
        const stop = gradientFill.gradientStops[0];
        if (stop && stop.color)
          style += `background: ${colorToCss(stop.color)};`;
      }
    }
  }
  // For VECTOR nodes: use fills for color and background-color
  else if (node.type === 'VECTOR') {
    if (node.fills && node.fills.length > 0) {
      const fill = node.fills.find(
        (f: any) =>
          f.type === 'SOLID' &&
          (f.visible !== false || typeof f.visible === 'undefined'),
      );
      if (fill && fill.color) style += `color: ${colorToCss(fill.color)};`;
      const bgFill = node.fills.find(
        (f: any) =>
          f.type === 'SOLID' &&
          (f.visible !== false || typeof f.visible === 'undefined'),
      );
      if (bgFill && bgFill.color && !isImage)
        style += `background-color: ${colorToCss(bgFill.color)};`;
    }
  }
  // For SECTION nodes: use fills for background-color
  else if (node.type === 'SECTION') {
    if (node.fills && node.fills.length > 0) {
      const fill = node.fills.find(
        (f: any) =>
          f.type === 'SOLID' &&
          (f.visible !== false || typeof f.visible === 'undefined'),
      );
      if (fill && fill.color && !isImage)
        style += `background-color: ${colorToCss(fill.color)};`;
    }
  }
  // For non-images and non-buttons, apply background
  else if (
    !isImage &&
    node.visible !== false &&
    node.fills &&
    node.fills.length > 0
  ) {
    const fill = node.fills.find(
      (f: any) =>
        f.type === 'SOLID' &&
        (f.visible !== false || typeof f.visible === 'undefined'),
    );
    if (fill && fill.color && !isImage)
      style += `background: ${colorToCss(fill.color)};`;
  }

  // For non-buttons, apply border if present
  if (!isButton && node.strokes && node.strokes.length > 0) {
    const stroke = node.strokes.find(
      (s: any) =>
        s.type === 'SOLID' &&
        (s.visible !== false || typeof s.visible === 'undefined'),
    );
    if (stroke && stroke.color)
      style += `border: 1px solid ${colorToCss(stroke.color)};`;
  }

  if (node.opacity !== undefined) style += `opacity: ${node.opacity};`;
  if (node.effects && node.effects.length > 0) {
    node.effects.forEach((effect: any) => {
      if (effect.type === 'DROP_SHADOW' && effect.visible) {
        style += `box-shadow: ${effect.offset.x}px ${effect.offset.y}px ${
          effect.radius
        }px ${colorToCss(effect.color)};`;
      }
    });
  }
  let hasPadding =
    (node.paddingLeft && node.paddingLeft > 0) ||
    (node.paddingRight && node.paddingRight > 0) ||
    (node.paddingTop && node.paddingTop > 0) ||
    (node.paddingBottom && node.paddingBottom > 0);
  if (hasPadding) {
    style += 'box-sizing: border-box;';
    if (node.paddingLeft && node.paddingLeft > 0)
      style += `padding-left: ${node.paddingLeft}px;`;
    if (node.paddingRight && node.paddingRight > 0)
      style += `padding-right: ${node.paddingRight}px;`;
    if (node.paddingTop && node.paddingTop > 0)
      style += `padding-top: ${node.paddingTop}px;`;
    if (node.paddingBottom && node.paddingBottom > 0)
      style += `padding-bottom: ${node.paddingBottom}px;`;
  }
  if (node.layoutMode) {
    style += applyDisplayStyles(node);
  } else {
    if (node.layoutAlign === 'INHERIT') {
      style += 'display: inherit;';
      style += 'gap: inherit;';
      style += 'align-items: inherit;';
      style += 'justify-content: inherit;';
      style += 'flex-direction: inherit;';
    }
  }
  if (node.marginLeft) style += `margin-left: ${node.marginLeft}px;`;
  if (node.marginRight) style += `margin-right: ${node.marginRight}px;`;
  if (node.marginTop) style += `margin-top: ${node.marginTop}px;`;
  if (node.marginBottom) style += `margin-bottom: ${node.marginBottom}px;`;
  if (node.type === 'TEXT') style += textStyleFromNode(node);
  return style;
}

function applyDisplayStyles(node: FigmaNode) {
  let style = '';
  style += `display: flex; flex-direction: ${
    node.layoutMode === 'VERTICAL' ? 'column' : 'row'
  };`;

  // Alignment logic
  if (node.primaryAxisAlignItems) {
    switch (node.primaryAxisAlignItems) {
      case 'CENTER':
        style += 'justify-content: center;';
        break;
      case 'MIN':
        style += 'justify-content: flex-start;';
        break;
      case 'MAX':
        style += 'justify-content: flex-end;';
        break;
      case 'SPACE_BETWEEN':
        style += 'justify-content: space-between;';
        break;
    }
  }
  if (node.counterAxisAlignItems) {
    switch (node.counterAxisAlignItems) {
      case 'CENTER':
        style += 'align-items: center; text-align: center;';
        break;
      case 'MIN':
        style += 'align-items: flex-start;';
        break;
      case 'MAX':
        style += 'align-items: flex-end;';
        break;
      case 'BASELINE':
        style += 'align-items: baseline;';
        break;
    }
  }
  if (
    node.itemSpacing &&
    node.primaryAxisAlignItems !== 'SPACE_BETWEEN' &&
    (node.parent?.layoutMode === 'HORIZONTAL' ||
      node.parent?.layoutMode === 'VERTICAL')
  ) {
    style += `gap: ${node.itemSpacing}px;`;
  }
  return style;
}

function textStyleFromNode(node: FigmaNode): string {
  let style = '';
  if (node.style) {
    if (node.style.fontFamily)
      style += `font-family: ${node.style.fontFamily};`;
    if (node.style.fontSize) style += `font-size: ${node.style.fontSize}px;`;
    if (node.style.fontWeight)
      style += `font-weight: ${node.style.fontWeight};`;
    if (node.style.lineHeightPx)
      style += `line-height: ${node.style.lineHeightPx}px;`;
    if (node.style.letterSpacing)
      style += `letter-spacing: ${node.style.letterSpacing}px;`;
    if (node.style.textAlignHorizontal)
      style += `text-align: ${node.style.textAlignHorizontal.toLowerCase()};`;
    if (node.style.textAlignVertical)
      style += `vertical-align: ${node.style.textAlignVertical.toLowerCase()};`;
    if (node.style.textAutoResize === 'NONE') {
      if (node.style.textTruncation === 'DISABLED') {
        style += `white-space: normal; overflow: visible; text-overflow: clip;`;
      }
      if (node.style.textTruncation === 'ENDING') {
        style += `white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
      }
    }
    if (node.style.textAutoResize === 'WIDTH') {
      if (node.style.textTruncation === 'DISABLED') {
        style += `display: inline-block; width: auto; white-space: nowrap;`;
      }
      if (node.style.textTruncation === 'ENDING') {
        style += `display: inline-block; width: auto; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
      }
    }
    if (node.style.textAutoResize === 'HEIGHT') {
      if (node.style.textTruncation === 'DISABLED') {
        style += `white-space: normal; height: auto;`;
      }
      if (node.style.textTruncation === 'ENDING') {
        style += `display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;`;
      }
    }
    if (node.style.textAutoResize === 'WIDTH_AND_HEIGHT') {
      if (node.style.textTruncation === 'DISABLED') {
        style += `width: auto; height: auto; white-space: normal;`;
      }
      if (node.style.textTruncation === 'ENDING') {
        style += `display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;`;
      }
    }
    if (node.style.textAutoResize === 'TRUNCATE') {
      if (node.style.textTruncation === 'DISABLED') {
        style += `white-space: normal; overflow: visible; text-overflow: clip;`;
      }
      if (node.style.textTruncation === 'ENDING') {
        style += `white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
      }
    }
  }
  if (node.fills && node.fills.length > 0) {
    const fill = node.fills.find(
      (f: any) => f.type === 'SOLID' && f.visible !== false,
    );
    if (fill && fill.color) style += `color: ${colorToCss(fill.color)};`;
  }
  return style;
}

async function fetchFigmaImageUrl(
  fileKey: string,
  nodeId: string,
  designToCode: DesignToCodeApi,
): Promise<string | null> {
  //const url = `https://api.figma.com/v1/images/${fileKey}?ids=${nodeId}&format=svg`;
  try {
    const data = await designToCode.fetchImageFromFigmaDesign(fileKey, nodeId);
    return data;
  } catch {
    return null;
  }
}

function generateUnique3LetterCode() {
  const letters = 'abcdefghijklmnopqrstuvwxyz'.split('');

  // Shuffle the letters array
  for (let i = letters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [letters[i], letters[j]] = [letters[j], letters[i]];
  }

  // Take the first 3 unique letters
  return letters.slice(0, 3).join('');
}
// Recursively parse node and generate HTML/CSS
// Generate short class names based on node id or name
function getShortClassName(node: FigmaNode): string {
  const uniqueCode = generateUnique3LetterCode();
  if (node.name) {
    // Use first 8 chars of name, alphanumeric only
    return (
      'c' +
      node.name
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 8)
        .toLowerCase() +
      '_' +
      uniqueCode
    );
  }
  if (node.id) {
    // Use last part of id and prefix 'c'
    const idPart = node.id.split(':').pop();
    return `c${idPart}_${uniqueCode}`;
  }
  return 'cnode_' + uniqueCode;
}

async function parseNodeAsync(
  node: FigmaNode,
  cssArr: string[],
  fileKey: string,
  designToCode: DesignToCodeApi,
): Promise<string> {
  if (node.visible === false) return '';
  let html = '';
  let className = getShortClassName(node);
  //if (parentClass) className = parentClass + '__' + className;

  const lowerName = (node.name || '').toLowerCase();

  if (
    node.type === 'BUTTON' ||
    (node.type === 'INSTANCE' && lowerName === 'button')
  ) {
    cssArr.push(`.${className} { ${styleFromNode(node, false, true)} }`);
    let buttonLabel = '';
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        if (child.type === 'TEXT') {
          buttonLabel += child.characters || '';
        } else if (
          child.name &&
          child.name.toLowerCase().includes('label') &&
          child.children &&
          child.children.length > 0
        ) {
          for (const labelChild of child.children) {
            if (labelChild.type === 'TEXT') {
              buttonLabel += labelChild.characters || '';
            }
          }
        } else {
          buttonLabel += await extractTextFromNode(child);
        }
      }
    }
    html += `<button class="${className}">${buttonLabel.trim()}</button>`;
  }
  // Label
  else if (lowerName.includes('label')) {
    cssArr.push(`.${className} { ${styleFromNode(node, false, false)} }`);
    html += `<label class="${className}">`;
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        html += await parseNodeAsync(child, cssArr, fileKey, designToCode);
      }
    } else {
      html += node.characters || '';
    }
    html += `</label>`;
  }
  // Input
  else if (lowerName.includes('input')) {
    if (!node?.strokes?.length) {
      let parent = node.parent;
      while (parent && parent.type === 'FRAME') {
        if (parent.strokes && parent.strokes.length > 0) {
          node.strokes = parent.strokes;
          node.absoluteBoundingBox = parent.absoluteBoundingBox;
          node.absoluteRenderBounds = parent.absoluteRenderBounds;
          node.fills = parent.fills;
          node.background = parent.background;
          node.cornerRadius = parent.cornerRadius;
          node.cornerSmoothing = parent.cornerSmoothing;
          break;
        }
        parent = parent.parent;
      }
    }
    cssArr.push(`.${className} { ${styleFromNode(node, false, false)} }`);
    const textContent =
      node.children
        ?.filter((child: FigmaNode) => child.type === 'TEXT')
        ?.map((child: FigmaNode) => child.characters)
        .join('') || '';
    html += `<input class="${className}" value="${textContent}" />`;
  }
  // Image or Vector (SVG replaced with <img>)
  else if (
    lowerName.includes('image') ||
    node.type === 'IMAGE' ||
    (node.fills && node.fills[0] && node.fills[0].type === 'IMAGE') ||
    (node.type === 'VECTOR' && node.id)
  ) {
    cssArr.push(`.${className} { ${styleFromNode(node, true, false)} }`);
    let imageUrl = '';
    if (node.id && fileKey && designToCode) {
      // imageUrl =
      //   (await fetchFigmaImageUrl(fileKey, node.id, designToCode)) || '';
      imageUrl = node.id;
      if (!imagesCache.includes(imageUrl)) {
        imagesCache.push(imageUrl);
      }
    }
    html += imageUrl
      ? `<img class="${className}" src="${imageUrl}" alt="${
          node.name || ''
        }" />`
      : '';
  }
  // Rectangle, Frame, Instance, Group, Component
  else if (
    node.type === 'RECTANGLE' ||
    node.type === 'FRAME' ||
    node.type === 'INSTANCE' ||
    node.type === 'GROUP' ||
    node.type === 'COMPONENT'
  ) {
    cssArr.push(`.${className} { ${styleFromNode(node, false, false)} }`);
    // Avoid unnecessary wrapper divs: if only one child and it's a text node, render directly
    if (
      node.children &&
      node.children.length === 1 &&
      node.children[0].type === 'TEXT'
    ) {
      html += await parseNodeAsync(
        node.children[0],
        cssArr,
        fileKey,
        designToCode,
      );
    } else {
      html += `<div class="${className}">`;
      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          html += await parseNodeAsync(child, cssArr, fileKey, designToCode);
        }
      }
      html += `</div>`;
    }
  }
  // Text
  else if (node.type === 'TEXT') {
    cssArr.push(`.${className} { ${textStyleFromNode(node)} }`);
    if (lowerName.includes('label')) {
      html += `<label class="${className}">${node.characters || ''}</label>`;
    } else {
      html += `<span class="${className}">${node.characters || ''}</span>`;
    }
  }
  // Add support for Figma API import root node type (commonly 'DOCUMENT' or 'CANVAS')
  else if (
    node.type === 'DOCUMENT' ||
    node.type === 'CANVAS' ||
    node.type === 'PAGE'
  ) {
    // These are root/container nodes, render children only, no wrapper
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        html += await parseNodeAsync(child, cssArr, fileKey, designToCode);
      }
    }
  }
  // Default fallback
  else {
    if (node.children && node.children.length > 0) {
      html += `<div class="${className}">`;
      for (const child of node.children) {
        html += await parseNodeAsync(child, cssArr, fileKey, designToCode);
      }
      html += `</div>`;
    }
  }
  return html;
}

// Helper to extract text recursively from any node (for button labels)
async function extractTextFromNode(node: FigmaNode): Promise<string> {
  let text = '';
  if (node.type === 'TEXT') {
    text += node.characters || '';
  } else if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      text += await extractTextFromNode(child);
    }
  }
  return text;
}

// Extract nodes from Figma API JSON format
function extractNodesFromFigmaApiJson(figmaApiJson: any): FigmaNode[] {
  if (!figmaApiJson.nodes) return [];
  let nodes: FigmaNode[] = [];
  for (const nodeId in figmaApiJson.nodes) {
    const nodeObj = figmaApiJson.nodes[nodeId];
    if (nodeObj && nodeObj.document) {
      nodes.push(nodeObj.document);
    }
  }
  nodes = removeInvisibleNodes(structuredClone(nodes));
  return nodes;
}

function removeInvisibleNodes(
  nodes: FigmaNode[],
  parentNode: FigmaNode = null,
): FigmaNode[] {
  const visibleNodes: FigmaNode[] = nodes.filter(
    node => node.visible !== false,
  );
  for (const node of visibleNodes) {
    node.parent = parentNode;
    if (node.children && node.children.length > 0) {
      node.children = removeInvisibleNodes(node.children, node);
    }
  }
  return visibleNodes;
}

// Main entry for Figma API JSON format
export async function figmaApiJsonToHtmlCss(
  figmaApiJson: any,
  fileKey: string,
  designToCode: DesignToCodeApi,
): Promise<HtmlCssResult> {
  imagesCache = [];
  const cssArr: string[] = [];
  let html = '';
  const nodes = extractNodesFromFigmaApiJson(figmaApiJson);
  for (const node of nodes) {
    html += await parseNodeAsync(node, cssArr, fileKey, designToCode);
  }

  html = await updateImagesData(fileKey, designToCode, html);
  imagesCache = [];
  return {
    html,
    css: cssArr.join('\n'),
  };
}

// For legacy format (selectedNodes)
export async function figmaJsonToHtmlCss(
  figmaApiJson: any,
  fileKey: string,
  designToCode: DesignToCodeApi,
): Promise<HtmlCssResult> {
  imagesCache = [];
  const cssArr: string[] = [];
  let html = '';
  const nodes = extractNodesFromFigmaApiJson(figmaApiJson);
  for (const node of nodes) {
    html += await parseNodeAsync(node, cssArr, fileKey, designToCode);
  }

  html = await updateImagesData(fileKey, designToCode, html);
  imagesCache = [];
  return {
    html,
    css: cssArr.join('\n'),
  };
}

async function updateImagesData(
  fileKey: string,
  designToCode: DesignToCodeApi,
  html: string,
): Promise<string> {
  if (imagesCache.length > 0) {
    const imagesData: any = await fetchFigmaImageUrl(
      fileKey,
      imagesCache.join(','),
      designToCode,
    );

    if (imagesData?.images) {
      return html.replace(
        /src="([^"]*)"/g,
        (match, p1) => `src="${imagesData.images[p1]}"`,
      );
    }
  }
  return html;
}
